import React, { createContext, useContext, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';

const ACTION_CLOSE = 'close';
const ACTION_SET_PROMISE_RESPONSE = 'promiseResponse';
const ACTION_OPEN = 'open';
const ACTION_SET_MODAL_PROPS = 'setModalProps';

const initialState = {
    openedModalID: null,
    modalProps: null,
    onResolve: null,
    onReject: null,
};

const initialContext = {
    ...initialState,
    open: null,
    close: null,
    setModalProps: null,
};

const ModalManagerContext = createContext(initialContext);

function reducer(state, action) {
    switch (action.type) {
        case ACTION_CLOSE:
            return {
                ...state,
                onReject: null,
                onResolve: null,
                openedModalID: null,
                modalProps: null,
            };
        case ACTION_SET_PROMISE_RESPONSE:
            return {
                ...state,
                onResolve: action.payload.onResolve,
                onReject: action.payload.onReject,
            };
        case ACTION_OPEN:
            return {
                ...state,
                openedModalID: action.payload.openedModalID,
                modalProps: action.payload.modalProps,
                onReject: null,
                onResolve: null,
            };
        case ACTION_SET_MODAL_PROPS:
            return {
                ...state,
                modalProps: action.payload.modalProps,
            };
        default:
            return state;
    }
}

export const ModalManager = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const close = () => {
        dispatch({ type: ACTION_CLOSE });
    };

    const generatePromise = useCallback(
        () =>
            new Promise((resolve, reject) => {
                const setOnResolve = (data) => {
                    close();
                    resolve(data);
                };
                const setOnReject = (data) => {
                    close();
                    reject(data);
                };

                dispatch({
                    type: ACTION_SET_PROMISE_RESPONSE,
                    payload: {
                        onResolve: setOnResolve,
                        onReject: setOnReject,
                    },
                });
            }),
        [],
    );

    const open = (id, props) => {
        dispatch({
            type: ACTION_OPEN,
            payload: {
                openedModalID: id,
                modalProps: props,
            },
        });

        return generatePromise();
    };

    const setModalProps = (props) => {
        dispatch({
            type: ACTION_SET_MODAL_PROPS,
            payload: {
                modalProps: props,
            },
        });
    };

    return (
        <ModalManagerContext.Provider
            value={{
                ...state,
                setModalProps,
                open,
                close,
            }}
        >
            {children}
        </ModalManagerContext.Provider>
    );
};

ModalManager.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useModal = () => useContext(ModalManagerContext);

export const Modal = ({ children, modalId }) => {
    const { openedModalID, modalProps, onResolve, onReject } = useModal();
    const opened = openedModalID === modalId;
    return typeof children === 'function'
        ? children({ ...modalProps, isOpen: opened, onResolve, onReject })
        : React.cloneElement(children, {
              ...modalProps,
              isOpen: opened,
              onResolve,
              onReject,
          });
};
